import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconEdit,
  IconGiftCard,
  IconPlayerPause,
  IconPlus,
} from '@tabler/icons-react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

const normalizarCodigo = (value = '') => String(value ?? '')
  .trim()
  .replace(/\s+/g, '')
  .toUpperCase();

const getFechaLegible = (value) => {
  if (!value) return 'Sin fecha';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const toDateInputValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isCouponExpired = (coupon) => {
  const endsAt = toDateInputValue(coupon.endsAt);
  return endsAt ? endsAt.getTime() < Date.now() : false;
};

const isCouponExhausted = (coupon) => {
  const maxUses = Number(coupon.maxUses || 0);
  return maxUses > 0 && Number(coupon.usedCount || 0) >= maxUses;
};

const getCouponStatus = (coupon) => {
  if (coupon.active === false) return { label: 'Pausado', color: 'gray' };
  if (isCouponExpired(coupon)) return { label: 'Vencido', color: 'red' };
  if (isCouponExhausted(coupon)) return { label: 'Agotado', color: 'orange' };
  return { label: 'Activo', color: 'green' };
};

const GiftcardCoupons = () => {
  const { currentUser } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const form = useForm({
    initialValues: {
      codeNormalized: '',
      percentOff: 10,
      active: true,
      maxUses: 0,
      startsAt: null,
      endsAt: null,
    },
    validate: {
      codeNormalized: (value) => {
        const code = normalizarCodigo(value);
        if (!code) return 'El código es obligatorio';
        if (!/^[A-Z0-9_-]+$/.test(code)) return 'Usa solo letras, números, guion o guion bajo';
        return null;
      },
      percentOff: (value) => {
        const percent = Number(value);
        if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
          return 'El descuento debe estar entre 1 y 100';
        }
        return null;
      },
      maxUses: (value) => {
        const maxUses = Number(value || 0);
        if (!Number.isInteger(maxUses) || maxUses < 0) {
          return 'El límite debe ser 0 o un entero positivo';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    const couponsQuery = query(collection(db, 'giftcardCoupons'), orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(
      couponsQuery,
      (snapshot) => {
        setCoupons(snapshot.docs.map((couponDoc) => ({
          id: couponDoc.id,
          ...couponDoc.data(),
        })));
        setLoading(false);
        setError('');
      },
      (snapshotError) => {
        console.error('Error al cargar cupones de giftcards:', snapshotError);
        setError('No se pudieron cargar los cupones de giftcards.');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const stats = useMemo(() => {
    const active = coupons.filter((coupon) => getCouponStatus(coupon).label === 'Activo').length;
    const paused = coupons.filter((coupon) => coupon.active === false).length;
    const totalUses = coupons.reduce((total, coupon) => total + Number(coupon.usedCount || 0), 0);
    return { active, paused, totalUses };
  }, [coupons]);

  const openCreateModal = () => {
    setEditingCoupon(null);
    form.setValues({
      codeNormalized: '',
      percentOff: 10,
      active: true,
      maxUses: 0,
      startsAt: null,
      endsAt: null,
    });
    form.resetDirty();
    setModalOpen(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    form.setValues({
      codeNormalized: coupon.codeNormalized || coupon.id,
      percentOff: Number(coupon.percentOff || 10),
      active: coupon.active !== false,
      maxUses: Number(coupon.maxUses || 0),
      startsAt: toDateInputValue(coupon.startsAt),
      endsAt: toDateInputValue(coupon.endsAt),
    });
    form.resetDirty();
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    const code = normalizarCodigo(values.codeNormalized);
    const couponRef = doc(db, 'giftcardCoupons', code);
    const isEditing = Boolean(editingCoupon);

    try {
      setSaving(true);
      await setDoc(
        couponRef,
        {
          codeNormalized: code,
          percentOff: Number(values.percentOff),
          active: values.active !== false,
          maxUses: Number(values.maxUses || 0),
          usedCount: isEditing ? Number(editingCoupon.usedCount || 0) : 0,
          startsAt: values.startsAt || null,
          endsAt: values.endsAt || null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || '',
          ...(isEditing ? {} : {
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || '',
          }),
        },
        { merge: isEditing }
      );

      notifications.show({
        title: isEditing ? 'Cupón actualizado' : 'Cupón creado',
        message: `El cupón ${code} quedó listo para giftcards.`,
        color: 'green',
      });

      setModalOpen(false);
      setEditingCoupon(null);
    } catch (saveError) {
      console.error('Error al guardar cupón:', saveError);
      notifications.show({
        title: 'Error',
        message: 'No se pudo guardar el cupón.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async (coupon) => {
    try {
      await updateDoc(doc(db, 'giftcardCoupons', coupon.id), {
        active: false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || '',
      });

      notifications.show({
        title: 'Cupón pausado',
        message: `El cupón ${coupon.id} ya no aplicará descuentos.`,
        color: 'yellow',
      });
    } catch (pauseError) {
      console.error('Error al pausar cupón:', pauseError);
      notifications.show({
        title: 'Error',
        message: 'No se pudo pausar el cupón.',
        color: 'red',
      });
    }
  };

  return (
    <Container size="xl" py="md">
      <Paper shadow="sm" p={{ base: 'md', md: 'xl' }} radius="xl" withBorder className="surface-panel">
        <Group justify="space-between" align="flex-start" mb="xl" gap="md">
          <Stack gap={6} style={{ flex: 1, minWidth: 260 }}>
            <Badge color="yellow" variant="light" w="fit-content">
              Descuentos giftcard
            </Badge>
            <Title order={2} style={{ letterSpacing: '-0.03em' }}>
              Cupones giftcards
            </Title>
            <Text c="dimmed" maw={760}>
              Crea y pausa códigos porcentuales que la página pública valida desde backend al armar una giftcard.
            </Text>
          </Stack>
          <Button leftSection={<IconPlus size={18} />} onClick={openCreateModal}>
            Crear cupón
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="xl">
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Activos</Text>
            <Title order={3}>{stats.active}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Pausados</Text>
            <Title order={3}>{stats.paused}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Usos totales</Text>
            <Title order={3}>{stats.totalUses}</Title>
          </Card>
        </SimpleGrid>

        {error && (
          <Alert icon={<IconAlertCircle size={18} />} color="red" mb="lg">
            {error}
          </Alert>
        )}

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
            <Text c="dimmed">Cargando cupones...</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={920}>
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Código</Table.Th>
                  <Table.Th>Descuento</Table.Th>
                  <Table.Th>Usos</Table.Th>
                  <Table.Th>Vigencia</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Actualización</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coupons.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="xl">Todavía no hay cupones giftcard.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : coupons.map((coupon) => {
                  const status = getCouponStatus(coupon);
                  const maxUses = Number(coupon.maxUses || 0);
                  const usedCount = Number(coupon.usedCount || 0);

                  return (
                    <Table.Tr key={coupon.id}>
                      <Table.Td>
                        <Text fw={900} ff="monospace">{coupon.codeNormalized || coupon.id}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={800}>{Number(coupon.percentOff || 0)}%</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={700}>{usedCount} / {maxUses || 'sin límite'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text size="sm">Inicio: {getFechaLegible(coupon.startsAt)}</Text>
                          <Text size="sm">Fin: {getFechaLegible(coupon.endsAt)}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={status.color} variant="light">{status.label}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text size="sm">{getFechaLegible(coupon.updatedAt)}</Text>
                          <Text size="xs" c="dimmed">{coupon.updatedBy || coupon.createdBy || 'Sin usuario'}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end" gap="xs" wrap="nowrap">
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconEdit size={14} />}
                            onClick={() => openEditModal(coupon)}
                          >
                            Editar
                          </Button>
                          {coupon.active !== false && (
                            <Button
                              size="xs"
                              color="yellow"
                              variant="light"
                              leftSection={<IconPlayerPause size={14} />}
                              onClick={() => handlePause(coupon)}
                            >
                              Pausar
                            </Button>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCoupon ? 'Editar cupón giftcard' : 'Crear cupón giftcard'}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Código"
              description="Se guardará en mayúsculas y sin espacios. Ej: CLUB10"
              disabled={Boolean(editingCoupon)}
              {...form.getInputProps('codeNormalized')}
              onChange={(event) => form.setFieldValue('codeNormalized', normalizarCodigo(event.currentTarget.value))}
            />
            <NumberInput
              label="Porcentaje de descuento"
              min={1}
              max={100}
              suffix="%"
              {...form.getInputProps('percentOff')}
            />
            <NumberInput
              label="Límite de usos"
              description="Usa 0 para dejarlo sin límite."
              min={0}
              step={1}
              {...form.getInputProps('maxUses')}
            />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <DateInput
                label="Inicio"
                clearable
                valueFormat="DD/MM/YYYY"
                {...form.getInputProps('startsAt')}
              />
              <DateInput
                label="Término"
                clearable
                valueFormat="DD/MM/YYYY"
                {...form.getInputProps('endsAt')}
              />
            </SimpleGrid>
            <Switch
              label="Cupón activo"
              description="Si está pausado, la página no aplicará el descuento."
              checked={form.values.active}
              onChange={(event) => form.setFieldValue('active', event.currentTarget.checked)}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" color="gray" onClick={() => setModalOpen(false)} type="button">
                Cancelar
              </Button>
              <Button leftSection={<IconGiftCard size={16} />} loading={saving} type="submit">
                {editingCoupon ? 'Guardar cambios' : 'Crear cupón'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
};

export default GiftcardCoupons;
